import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import styled, { css } from "styled-components/macro";

import { faTimes } from "@fortawesome/pro-regular-svg-icons";

import { colors } from "UI/colors";

import {
  overlayFadeIn,
  overlayFadeOut,
  popoverSlideIn,
  popoverSlideOut,
} from "./NotePopoverKeyframes";

const NotePopover = ({ isOpen, onClose, noteId }) => {
  const iframeRef = useRef(null);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isReadyToShow, setIsReadyToShow] = useState(false);

  // Handling states used for animation and rendering
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      setIsReadyToShow(false);
    } else if (shouldRender) {
      setIsClosing(true);
      // Wait for exit animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (shouldRender) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [shouldRender, onClose]);

  if (!shouldRender) return null;

  const noteUrl = `/note/${noteId}?isPreview=true`;

  const handleIframeLoad = () => {
    setIsReadyToShow(true);
  };

  return (
    <Overlay
      onClick={onClose}
      $isClosing={isClosing}
      $isReadyToShow={isReadyToShow}
    >
      <PopoverContainer
        onClick={(e) => e.stopPropagation()}
        $isClosing={isClosing}
        $isReadyToShow={isReadyToShow}
      >
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} size="sm" color={colors.white} />
        </CloseButton>
        <IframeContainer>
          <NoteIframe
            ref={iframeRef}
            src={noteUrl}
            title="Note"
            allowFullScreen
            onLoad={handleIframeLoad}
          />
        </IframeContainer>
      </PopoverContainer>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 10000;
  animation: ${({ $isClosing, $isReadyToShow }) =>
    $isClosing
      ? css`
          ${overlayFadeOut} 0.4s ease-out both
        `
      : $isReadyToShow
      ? css`
          ${overlayFadeIn} 0.6s ease
        `
      : css`
          ${overlayFadeIn} 0.6s ease
        `};
`;

const PopoverContainer = styled.div`
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  width: 95vw;
  max-width: 1118px;
  height: 95vh;
  display: flex;
  flex-direction: column;
  transform-origin: 50% 100%;
  animation: ${({ $isClosing, $isReadyToShow }) =>
    $isClosing
      ? css`
          ${popoverSlideOut} 0.4s ease-in both
        `
      : $isReadyToShow
      ? css`
          ${popoverSlideIn} 0.3s ease-in both
        `
      : css`
          none
        `};
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  transform: ${({ $isReadyToShow }) =>
    $isReadyToShow ? "translateY(0)" : "translateY(50vh)"};
  opacity: ${({ $isReadyToShow }) => ($isReadyToShow ? 1 : 0)};
  border-radius: 10px 10px 0 0;

  @media (max-width: 768px) {
    height: 95vh;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 6px;
  left: 5px;
  background: rgb(205, 205, 205);
  cursor: pointer;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, transform 0.1s ease;
  z-index: 10;
  border: none;

  &:hover {
    background-color: rgb(183, 183, 183);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const IframeContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  border-radius: 10px 10px 0 0;
`;

const NoteIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
  border-radius: 10px 10px 0 0;
`;

NotePopover.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  noteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default NotePopover;
